// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../local-values/utils/countries';
import classNames from 'classnames/bind';
import Select from '../../../../elements/Select/Select';
import Modal from '../../../../elements/Modal/Modal';
import Loading from '../../../../elements/Loading/Loading';
import {
  getPayoutMethodsApi, searchAdmin1Api, searchAdmin2Api, searchAdmin3Api, getBanksApi, endpoints
} from '../api';
import CountryStep from './CountryStep/CountryStep';
import MethodStep from './MethodStep/MethodStep';
import AccountStep from './AccountStep/AccountStep';
import { FormValidator } from '../../../../utils/form';
import update from 'immutability-helper';
import { getAddressFormValidator } from '../../../../utils/address';
import DotsMenu from '../../../../elements/DotsMenu/DotsMenu';
import StaticMap from '../../../../elements/Map/StaticMap';
import PayPalStep from './PayPalStep/PayPalStep';
import Cleave from 'cleave.js/dist/cleave-react';

import commonStyle from '../../PreferencesViewCommon.scss';
import modalStyle from '../../../../assets/styles/modal.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import icons from '../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import style from './Payouts.scss';
import formStyle from '../../../../assets/styles/form.scss';
import paypal from '../../../../assets/img/home/pay-methods/paypal.png';
import bbva from '../../../../assets/img/home/pay-methods/bbva.png';
import bcp from '../../../../assets/img/home/pay-methods/bcp.png';
import interbank from '../../../../assets/img/home/pay-methods/interbank.png';
import scotiabank from '../../../../assets/img/home/pay-methods/scotiabank.png';

import type { Bank, Country, Admin, Payout, DisbursementMethod } from '../../../../entities';
import type { Option } from '../../../../elements/Select/Select';
import type { FormValidationResult } from '../../../../utils/form';
import type { HTTPError } from '../../../../error';
import type { AddressRequest } from '../../saved-addresses/api';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +postPayout: (payout: Payout) => void;
  +postPayoutPayPal: (email: string, country: string) => void;
  +deletePayout: () => void;
  +payouts?: Payout;
  +successDelete?: boolean;
  +isPostingPayout?: boolean;
  +isDeletingPayout?: boolean;
  +deletePayoutError?: HTTPError;
  +resetAlertState: () => void;
  +localValues: LocalValuesState;
};

type State = {
  deleteModalVisible: boolean;
  payout?: Payout;
  accountNumber: {
    value: string;
    errors?: string[];
  };
  accountNumberCci: {
    value: string;
    errors?: string[];
  };
  address1: {
    value: string;
    errors?: string[];
  };
  address2: {
    value: string;
  };
  admin1: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin2: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin3: {
    value: string;
    label?: string;
    errors?: string[];
  };
  admin1Items: Admin[];
  admin2Items: Admin[];
  admin3Items: Admin[];
  bank: {
    value: number;
    errors?: string[];
  };
  banks?: Bank[];
  country: {
    value: Country;
    errors?: string[];
  };
  firstName: {
    value: string;
    errors?: string[];
  };
  idNumber: {
    value: string;
    errors?: string[];
  };
  lastName: {
    value: string;
    errors?: string[];
  };
  isLoadingAdmin1?: boolean;
  isLoadingAdmin2?: boolean;
  isLoadingAdmin3?: boolean;
  method: {
    value: string;
    errors?: string[];
  };
  methods?: DisbursementMethod[];
  email: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  routingNumber: {
    value: string;
    errors?: string[];
  };
  step: 'country' | 'method' | 'account';
  zipCode: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  title: {
    id: 'preferences.payouts.document.title',
  },
  description: {
    id: 'preferences.payouts.document.description',
  },
  payouts: {
    title: {
      id: 'preferences.payouts.title'
    },
    subtitle: {
      id: 'preferences.payouts.subtitle',
    }
  },
  deleteWarning: {
    id: 'preferences.payouts.deleteWarning',
  },
  accountNumber: {
    label: {
      id: 'preferences.payouts.accountNumber.label',
    },
    placeholder: {
      id: 'preferences.payouts.accountNumber.placeholder',
    },
  },
  accountNumberCci: {
    label: {
      id: 'preferences.payouts.accountNumberCci.label',
    },
    placeholder: {
      id: 'preferences.payouts.accountNumberCci.placeholder',
    },
  },
  country: {
    label: {
      id: 'address.country.label',
    },
    placeholder: {
      id: 'address.country.placeholder',
    },
  },
  delete: {
    id: 'preferences.payouts.delete',
  },
  button: {
    cancel: {
      id: 'preferences.payouts.button.cancel',
    },
    delete: {
      id: 'preferences.payouts.button.delete'
    }
  }
});

const initialFormState = {
  accountNumber: { value: '' },
  accountNumberCci: { value: '', },
  address1: { value: '', },
  address2: { value: '', },
  admin1: { value: '', },
  admin2: { value: '', },
  admin3: { value: '', },
  bank: { value: 0, errors: undefined },
  banks: [],
  country: { value: '', errors: undefined, },
  firstName: { value: '', },
  idNumber: { value: '' },
  lastName: { value: '', },
  method: { value: '' },
  methods: [],
  email: { value: '', },
  phone: { value: '', },
  routingNumber: { value: '' },
  step: 'country',
  zipCode: { value: '' },
};

const banksImages = { '12': bbva, '13': bcp, '14': interbank, '15': scotiabank };

export default class Payouts extends Component<Props, State> {
  state = {
    deleteModalVisible: false,
    payout: null,
    admin1Items: [],
    admin2Items: [],
    admin3Items: [],
    isLoadingAdmin1: false,
    isLoadingAdmin2: false,
    isLoadingAdmin3: false,
    ...initialFormState,
  };
  formValidatorPayPal: FormValidator;
  phoneCleave: Cleave;

  constructor(props: Props) {
    super(props);
    this.formValidatorPayPal = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
    });
  }

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  componentDidUpdate(prevProps: Props) {
    const { successDelete, deletePayoutError } = this.props;
    if (!prevProps.successDelete && successDelete) {
      this.setState({
        step: 'country', ...update(initialFormState, {
          $unset: ['country', 'method']
        }),
        deleteModalVisible: false,
      });
    }
    if (!prevProps.deletePayoutError && deletePayoutError) {
      this.handleCloseDeleteModal();
    }
  }

  getBanks(country: Country) {
    getBanksApi(undefined, {
      route: endpoints.BANKS.replace(':country', country),
    }).toPromise().then((banks: Bank[]) => {
      return this.setState({ banks });
    }).catch((error: HTTPError) => {
      console.error(error);
    });
  }

  handleChangeCountry(option: Option) {
    const country = `${option ? option.value : ''}`;

    this.setState({
      country: {
        value: country,
      },
      admin1: {
        value: '',
      },
      admin1Items: undefined,
      isLoadingAdmin1: true,
      admin2: {
        value: '',
      },
      admin2Items: undefined,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });

    getPayoutMethodsApi(undefined, {
      route: endpoints.PAYOUT_METHODS.replace(':country', country),
    }).toPromise().then((methods: DisbursementMethod) => {
      return this.setState({ ...this.state, methods, country: { value: country } });
    }).catch((error: HTTPError) => console.error(error));

    this.getBanks(country);

    searchAdmin1Api(undefined, {
      route: endpoints.SEARCH_ADMIN1.replace(':country', country),
    }).toPromise().then((admin1Items: Admin[]) => {
      return this.setState({ admin1Items, isLoadingAdmin1: false });
    }).catch((error: HTTPError) => console.error(error));
  }

  handleChangeAdmin1(option: Option) {
    const { country } = this.state;
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin1: {
        value: `${option.value}`,
      },
      admin2: {
        value: '',
      },
      admin2Items: undefined,
      isLoadingAdmin2: true,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });
    searchAdmin2Api(undefined, {
      route: endpoints.SEARCH_ADMIN2.replace(':country/:admin1', `${country.value}/${option.value}`),
    }).toPromise().then((admin2Items: Admin[]) =>
      this.setState({ admin2Items, isLoadingAdmin2: false }))
      .catch((error: HTTPError) => console.error(error));
  }

  handleChangeAdmin2(option: Option) {
    const { country, admin1 } = this.state;
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin2: {
        value: `${option.value}`,
      },
      isLoadingAdmin3: true,
      admin3: {
        value: '',
      },
      admin3Items: undefined,
    });
    if (country.value !== 'US') {
      searchAdmin3Api(undefined, {
        route: endpoints.SEARCH_ADMIN3
          .replace(':country/:admin1/:admin2', `${country.value}/${admin1.value}/${option.value}`)
      }).toPromise().then((admin3Items: Admin[]) =>
        this.setState({ admin3Items, isLoadingAdmin3: false })
      ).catch((error: HTTPError) => console.error(error));
    }
  }

  handleChangeAdmin3(option: Option) {
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState({
      admin3: {
        value: `${option.value}`,
      },
    });
  }

  handleChangeBank(option: Option) {
    if (typeof option.value === 'number') {
      this.setState({
        bank: {
          value: option.value,
        },
      });
    }
  }

  handleOpenDeleteModal() {
    this.setState({
      deleteModalVisible: true,
    });
  }

  handleCloseDeleteModal() {
    this.setState({
      deleteModalVisible: false,
    });
  }

  createPayout() {
    const { postPayout } = this.props;
    const {
      accountNumber, accountNumberCci, address1, address2, admin1, admin2, admin3, bank, country, firstName, idNumber,
      lastName, method, phone, routingNumber, zipCode
    } = this.state;
    const errors = getAddressFormValidator(country.value, 'payout').validateForm({
      accountNumber: accountNumber.value,
      accountNumberCci: accountNumberCci.value,
      address1: address1.value,
      admin1: admin1.value,
      admin2: admin2.value,
      admin3: admin3.value,
      bank: bank.value,
      firstName: firstName.value,
      idNumber: idNumber.value,
      lastName: lastName.value,
      phone: phone.value,
      routingNumber: routingNumber.value,
      //zipCode: zipCode.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          }
        });
      });
    } else {
      const billingAddress: AddressRequest = {
        address1: address1.value,
        address2: address2.value,
        admin1: parseInt(admin1.value),
        admin2: parseInt(admin2.value),
        admin3: parseInt(admin3.value),
        country: country.value,
        firstName: firstName.value,
        idNumber: idNumber.value,
        lastName: lastName.value,
        phone: phone.value,
      };

      if (zipCode.value !== '') {
        billingAddress.zipCode = zipCode.value;
      }

      const payout: Payout = {
        accountNumber: accountNumber.value,
        accountNumberCci: accountNumberCci.value,
        bankId: parseInt(bank.value),
        billingAddress: address2.value ? billingAddress : update(billingAddress, { $unset: ['address2'] }),
        country: country.value,
        currency: 1,
        payoutMethod: method.value,
        routingNumber: routingNumber.value,
      };
      let newPayout: Payout = routingNumber.value ? payout : update(payout, { $unset: ['routingNumber'] });
      postPayout(accountNumberCci.value ? newPayout : update(newPayout, { $unset: ['accountNumberCci'] }));
    }
  }

  affiliatePayout() {
    const { postPayoutPayPal } = this.props;
    const { email, country } = this.state;
    const errors = this.formValidatorPayPal.validateForm({
      email: email.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          }
        });
      });
    } else {
      postPayoutPayPal(email.value, country.value);
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { step, method } = this.state;
    switch (step) {
      case 'country':
        return this.setState({ step: 'method' });
      case 'method':
        return this.setState({ step: 'account' });
      case 'account':
        if (method.value === 'BANK_ACCOUNT') {
          this.createPayout();
        } else this.affiliatePayout();
        break;
      default:
        return;
    }
  }

  handleBack(e: Event) {
    e.preventDefault();
    const { step } = this.state;
    switch (step) {
      case 'method':
        return this.setState({
          step: 'country',
          method: { value: '' },
        });
      case 'account':
        return this.setState((prevState: State) => update(initialFormState, {
          banks: { $set: prevState.banks },
          method: { $set: prevState.method },
          methods: { $set: prevState.methods },
          country: { $set: prevState.country },
          step: { $set: 'method' },
        }));
      default:
        return;
    }
  }

  handleDelete() {
    const { deletePayout } = this.props;
    deletePayout();
  }

  handleChange(e: SyntheticInputEvent<HTMLInputElement>) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleChangePhone(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { value, rawValue } = e.target;
      if (value.length <= 15) {
        this.setState({ ['phone']: { value: rawValue } });
        this.phoneCleave.setRawValue(rawValue);
      }
      if (rawValue.length > 15) {
        this.phoneCleave.setRawValue(this.state.phone.value);
      }
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'phone') {
        if (value.length <= 15) {
          this.setState({ [name]: { value: rawValue } });
          this.phoneCleave.setRawValue(rawValue);
        }
        if (rawValue.length > 15) {
          this.phoneCleave.setRawValue(this.state.phone.value);
        }
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handlePhoneInit(cleave: any) {
    this.phoneCleave = cleave;
  }

  render() {
    const { payouts, isPostingPayout = false, isDeletingPayout, localValues } = this.props;
    const {
      country, step, method, methods, bank, banks, accountNumber, accountNumberCci, firstName, idNumber, lastName,
      email, phone, address1, address2, admin1, admin2, admin3, admin1Items, admin2Items, admin3Items,
      isLoadingAdmin1, isLoadingAdmin2, isLoadingAdmin3, deleteModalVisible, routingNumber, zipCode,
    } = this.state;
    const countryOptions: Option[] = (localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      value: country.key,
      image: country.icon,
      label: getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
    }));
    const countryError = country.errors && FormValidator.buildErrorLabel(country.errors, {
      required: 'address.country.error.required',
    });
    const staticMap =
      (<StaticMap center={country.value &&
      getLocalizedCountry(country.value, this.context.intl.formatMessage, localValues.countriesMap)}
                  defaultZoom={1}
                  width={376}
                  height={184}
                  className={style.mapContainer}/>);
    const countryContent = (
      <section className={formStyle.row}>
        <FormattedMessage id={messages.country.label.id}>{
          txt => (<label className={formStyle.label}>{txt}</label>)
        }</FormattedMessage>
        <Select options={countryOptions}
                className={classNames({
                  'light-blue': country.value,
                  'red': country.errors,
                }, style)}
                value={country.value}
                disabled={step === 'account'}
                searchable={true}
                placeholder={this.context.intl.formatMessage(messages.country.placeholder)}
                handleChange={this.handleChangeCountry.bind(this)}/>
        {countryError}
      </section>);
    const stepCountry = step === 'country' && (
      <CountryStep key="CountryStep"
                   disabled={country.value === ''}
                   countryContent={countryContent}
                   staticMap={staticMap}
                   handleSubmit={this.handleSubmit.bind(this)}/>);
    const stepMethod = step === 'method' && (
      <MethodStep key="MethodStep"
                  methods={methods || []}
                  method={method.value}
                  handleChange={this.handleChange.bind(this)}
                  handleBack={this.handleBack.bind(this)}
                  handleSubmit={this.handleSubmit.bind(this)}/>);
    const stepAccount = step === 'account' && method.value === 'BANK_ACCOUNT' ? (
        <AccountStep key="AccountStep"
                     banks={banks || []}
                     handleChangeBank={this.handleChangeBank.bind(this)}
                     handleChangeAdmin1={this.handleChangeAdmin1.bind(this)}
                     handleChangeAdmin2={this.handleChangeAdmin2.bind(this)}
                     handleChangeAdmin3={this.handleChangeAdmin3.bind(this)}
                     handleChange={this.handleChange.bind(this)}
                     handleChangePhone={this.handleChangePhone.bind(this)}
                     handleBack={this.handleBack.bind(this)}
                     handleSubmit={this.handleSubmit.bind(this)}
                     handleFocus={this.handleFocus.bind(this)}
                     handlePhoneInit={this.handlePhoneInit.bind(this)}
                     isPostingPayout={isPostingPayout}
                     accountNumber={accountNumber}
                     accountNumberCci={accountNumberCci}
                     address1={address1}
                     address2={address2}
                     admin1={admin1}
                     admin2={admin2}
                     admin3={admin3}
                     admin1Items={admin1Items}
                     admin2Items={admin2Items}
                     admin3Items={admin3Items}
                     bank={bank}
                     country={country}
                     firstName={firstName}
                     idNumber={idNumber}
                     lastName={lastName}
                     isLoadingAdmin1={isLoadingAdmin1}
                     isLoadingAdmin2={isLoadingAdmin2}
                     isLoadingAdmin3={isLoadingAdmin3}
                     phone={phone}
                     routingNumber={routingNumber}
                     zipCode={zipCode}/>) :
      <PayPalStep key="PayPalStep"
                  handleBack={this.handleBack.bind(this)}
                  handleSubmit={this.handleSubmit.bind(this)}
                  handleChange={this.handleChange.bind(this)}
                  handleFocus={this.handleFocus.bind(this)}
                  isPostingPayout={isPostingPayout}
                  email={email}/>;
    const menuOptions = [
      {
        labelIntl: 'preferences.payouts.box.menu.delete',
        icon: icons.iconCloseMedium,
        color: 'red',
        handler: (e: Event) => this.handleOpenDeleteModal(),
      }
    ];

    const payoutContent = payouts && (
      <article className={style.payoutWrapper}>
        <FormattedMessage id="preferences.payouts.title">{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id="preferences.payouts.subtitle">{
          txt => <p className={boxStyle.subtitle}>{txt}</p>
        }</FormattedMessage>
        <section className={style.box}>
          <div className={style.disbursementLogo}>
            <img src={payouts.bank ? banksImages[payouts.bank.disbursementBankId] : paypal}
                 alt={payouts.bank ? `${payouts.bank.name}` : "paypal"}/>
          </div>
          <p className={style.title}>{payouts.bank ? payouts.bank.name : payouts.paypalEmail}</p>
          {payouts.bank && <section className={style.accounts}>
            <section className={style.half}>
              <FormattedMessage id={messages.accountNumber.label.id}>{
                txt => <label className={style.label}>{txt}</label>
              }</FormattedMessage>
              <p className={style.accountCode}>{payouts.accountNumber}</p>
            </section>
            {payouts.bankCode && <section className={style.half}>
              <FormattedMessage id={messages.accountNumberCci.label.id}>{
                txt => <label className={style.label}>{txt}</label>
              }</FormattedMessage>
              <p className={style.accountCode}>{payouts.bankCode}</p>
            </section>}
          </section>}
          <DotsMenu orientation="left"
                    buttonStyle={style.dotsButton}
                    menuOptions={menuOptions}/>
        </section>
      </article>);
    return [
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>,
      <section key="payouts" className={classNames(boxStyle.box, commonStyle.contentWrap, style.contentWrapper)}>
        {payoutContent ||
        stepCountry ||
        stepMethod ||
        stepAccount}
      </section>,
      <Modal key="deleteModal"
             open={deleteModalVisible}
             height={180}
             handleClose={this.handleCloseDeleteModal.bind(this)}>
        {isDeletingPayout && <Loading type="sk-three-bounce"
                                      className={commonStyle.modalLoading}
                                      number={3}/>}
        {!isDeletingPayout && [
          <i key="icon" className={classNames(icons.symbolExclamationCircle, modalStyle.title)}/>,
          <FormattedMessage id={messages.deleteWarning.id} key="content">{
            txt => <p className={modalStyle.content}>{txt}</p>
          }</FormattedMessage>,
          <section className={modalStyle.buttonsWrap} key="wrap">
            <FormattedMessage id={messages.button.cancel.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                             onClick={this.handleCloseDeleteModal.bind(this)}
              >{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id={messages.button.delete.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red)}
                             onClick={this.handleDelete.bind(this)}
              >{txt}</button>
            }</FormattedMessage>
          </section>,
        ]}
      </Modal>
    ]
  }
}

Payouts.propTypes = {
  postPayout: PropTypes.func.isRequired,
  postPayoutPayPal: PropTypes.func.isRequired,
  deletePayout: PropTypes.func.isRequired,
  payouts: PropTypes.object,
  successDelete: PropTypes.bool,
  isPostingPayout: PropTypes.bool,
  isDeletingPayout: PropTypes.bool,
  deletePayoutError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
};

Payouts.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/payouts/components/Payouts.jsx